import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AccountModel } from '../../../../../../domain/models/account.model';
import {
  AccountBillingConfigDto,
  BillingConfigStatus,
  BillingFrequency
} from '../../../../../../domain/models/billing-config.model';
import { AccountBillingService, MissingPropertyLine } from '../../../../../../domain/services/account-billing/account-billing.service';
import { AccountCreditPanelComponent } from '../../../../../../common/account-credit-panel/account-credit-panel.component';

/** Account-scoped BILLING tab: configure billing and generate the first return. */
@Component({
  selector: 'app-billing-tab',
  standalone: true,
  imports: [
    CommonModule, FormsModule, MatButtonModule, MatIconModule, MatFormFieldModule,
    MatInputModule, MatSelectModule, MatCheckboxModule, MatProgressSpinnerModule,
    MatTooltipModule, AccountCreditPanelComponent
  ],
  templateUrl: './billing-tab.component.html',
  styleUrl: './billing-tab.component.css'
})
export class BillingTabComponent implements OnInit {
  @Input() account!: AccountModel;

  config: AccountBillingConfigDto | null = null;
  isLoading = true;
  loadError: string | null = null;
  saveError: string | null = null;
  saved = false;
  generating = false;
  generateMessage: string | null = null;
  missing: MissingPropertyLine[] = [];
  attaching = false;

  // Start period is validated inline (next to the field) instead of in the
  // bottom-of-page alert. The error is only surfaced once the field has been
  // touched or a save was attempted, not on initial load.
  startPeriodTouched = false;
  startPeriodServerError: string | null = null;
  private static readonly START_PERIOD_PATTERN = /^\d{4}-\d{2}$/;

  frequencies: BillingFrequency[] = ['MONTHLY', 'QUARTERLY', 'ANNUAL', 'SEASONAL'];
  statuses: BillingConfigStatus[] = ['ACTIVE', 'SUSPENDED', 'CLOSED'];

  form = this.defaults();

  constructor(private billingService: AccountBillingService) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    if (!this.account?.id) { this.isLoading = false; return; }
    this.isLoading = true;
    this.loadError = null;
    this.billingService.get(this.account.id).subscribe({
      next: (config) => {
        this.config = config;
        if (config) { this.applyToForm(config); }
        this.isLoading = false;
        this.loadMissing();
      },
      error: (err) => { console.error('Error loading billing config:', err); this.loadError = 'Unable to load billing config.'; this.isLoading = false; }
    });
  }

  get isSeasonal(): boolean {
    return this.form.frequency === 'SEASONAL';
  }

  get canGenerate(): boolean {
    return !!this.config && this.config.status === 'ACTIVE';
  }

  /** Shown below the Start period field once it has been touched or a save was attempted. */
  get startPeriodError(): string | null {
    if (this.startPeriodServerError) { return this.startPeriodServerError; }
    if (!this.startPeriodTouched) { return null; }
    const value = (this.form.startPeriod || '').trim();
    if (!value) { return 'Start period is required'; }
    if (!BillingTabComponent.START_PERIOD_PATTERN.test(value)) { return 'Start period must be YYYY-MM'; }
    return null;
  }

  savingConfig = false;

  /** One billing action at a time: overlapping saves/generates could overwrite each other's config. */
  get busy(): boolean {
    return this.savingConfig || this.backBilling || this.generating || this.attaching;
  }

  save(): void {
    if (!this.account?.id || this.busy) { return; }
    this.saveError = null;
    this.startPeriodServerError = null;
    this.saved = false;
    this.startPeriodTouched = true;
    if (this.startPeriodError) { return; }
    this.savingConfig = true;
    this.billingService.save(this.account.id, { ...this.form }).subscribe({
      next: (config) => { this.savingConfig = false; this.config = config; this.applyToForm(config); this.saved = true; },
      error: (err) => {
        this.savingConfig = false;
        console.error('Error saving billing config:', err);
        const message = err?.error?.message || 'Unable to save billing config.';
        if (/start period/i.test(message)) {
          this.startPeriodServerError = message;
        } else {
          this.saveError = message;
        }
      }
    });
  }

  generateFirstPeriod(): void {
    if (!this.account?.id || this.busy) { return; }
    this.generating = true;
    this.generateMessage = null;
    // Backfill every period from the start period up to the current month
    // (or the end period), not just the first.
    this.billingService.generateAllPeriods(this.account.id).subscribe({
      next: (res) => {
        this.generating = false;
        const n = res?.created ?? 0;
        this.generateMessage = n > 0 ? `Generated ${n} period${n === 1 ? '' : 's'}.` : 'No new periods to generate.';
        this.load();
      },
      error: (err) => { console.error('Error generating returns:', err); this.generating = false; this.generateMessage = err?.error?.message || 'Unable to generate returns.'; }
    });
  }

  loadMissing(): void {
    if (!this.account?.id) { return; }
    this.billingService.missingPropertyLines(this.account.id).subscribe({
      next: (rows) => this.missing = rows ?? [],
      error: (err) => console.error('Error checking properties on returns:', err)
    });
  }

  /** One line per property: "1 Main St — 2026-01, 2026-02 (2026-03 paid → supplemental)". */
  get missingByProperty(): { address: string; add: string[]; supplemental: string[]; conflict: string[] }[] {
    const map = new Map<number, { address: string; add: string[]; supplemental: string[]; conflict: string[] }>();
    for (const m of this.missing.filter(r => r.action !== 'BEFORE_BILLING_START')) {
      const g = map.get(m.propertyId) ?? { address: m.address, add: [], supplemental: [], conflict: [] };
      (m.action === 'ADD' ? g.add : m.action === 'BOUNDARY_CONFLICT' ? g.conflict : g.supplemental).push(m.period);
      map.set(m.propertyId, g);
    }
    return [...map.values()];
  }

  /** Properties renting before the billing start; `since` is the schedule-aligned start the server suggests. */
  get beforeBillingStart(): { address: string; since: string }[] {
    return this.missing.filter(r => r.action === 'BEFORE_BILLING_START')
      .map(r => ({ address: r.address, since: r.period }));
  }

  get earliestRentalPeriod(): string | null {
    const s = this.beforeBillingStart.map(r => r.since).sort();
    return s.length ? s[0] : null;
  }

  backBilling = false;

  /**
   * One staff action (Todd, pbstest 2026-10-05: "Use", then Save, then Generate read as three
   * unrelated steps): move the billing start to the server's schedule-aligned suggestion — the
   * normal save, so share/boundary checks still apply — then generate the back returns.
   */
  /** Only an ACTIVE schedule generates returns; otherwise the button would move the start for nothing. */
  get canBillBack(): boolean {
    return this.config?.status === 'ACTIVE' && !this.busy;
  }

  billBackPeriods(): void {
    const p = this.earliestRentalPeriod;
    if (!p || !this.account?.id || !this.config || !this.canBillBack) { return; }
    const accountId = this.account.id;
    this.backBilling = true;
    this.saveError = null;
    this.generateMessage = null;
    // Saved settings + the new start only: unsaved drafts (e.g. late interest switched on) are
    // never saved behind the staff's back, and stay in the form afterwards.
    this.billingService.save(accountId, { ...this.toRequest(this.config), startPeriod: p }).subscribe({
      next: (config) => {
        this.config = config;
        this.form.startPeriod = config.startPeriod;
        this.billingService.generateAllPeriods(accountId).subscribe({
          next: (res) => {
            this.backBilling = false;
            const n = res?.created ?? 0;
            this.generateMessage = `Billing now starts ${p}. Generated ${n} return${n === 1 ? '' : 's'}.`;
            this.loadMissing();
          },
          error: (err) => {
            this.backBilling = false;
            const why = err?.error?.message ? ` (${err.error.message})` : '';
            this.generateMessage = `Billing now starts ${p}, but generating the returns failed${why}. Use Generate returns to retry.`;
            this.loadMissing();
          }
        });
      },
      error: (err) => {
        this.backBilling = false;
        this.saveError = err?.error?.message || 'Unable to change the billing start.';
      }
    });
  }

  attachProperties(): void {
    if (!this.account?.id || this.busy) { return; }
    this.attaching = true;
    this.billingService.attachProperties(this.account.id).subscribe({
      next: (res) => {
        this.attaching = false;
        const n = res?.updated ?? 0;
        this.generateMessage = `Updated ${n} return${n === 1 ? '' : 's'} with the missing properties.`;
        this.loadMissing();
      },
      error: (err) => { console.error('Error adding properties to returns:', err); this.attaching = false; this.generateMessage = err?.error?.message || 'Unable to add properties to existing returns.'; }
    });
  }

  private applyToForm(c: AccountBillingConfigDto): void {
    this.form = this.toRequest(c);
  }

  /** The saved configuration as a save request (unsaved form drafts excluded). */
  private toRequest(c: AccountBillingConfigDto) {
    return {
      frequency: c.frequency,
      startPeriod: c.startPeriod,
      endPeriod: c.endPeriod ?? '',
      status: c.status,
      dueDayOfMonth: c.dueDayOfMonth,
      collectionAllowanceEnabled: c.collectionAllowanceEnabled,
      collectionAllowanceRate: c.collectionAllowanceRate,
      collectionAllowanceCap: c.collectionAllowanceCap,
      latePenaltyEnabled: c.latePenaltyEnabled,
      latePenaltyRate: c.latePenaltyRate,
      latePenaltyMin: c.latePenaltyMin,
      latePenaltyMaxPct: c.latePenaltyMaxPct,
      lateInterestEnabled: c.lateInterestEnabled,
      activeMonths: c.activeMonths ?? ''
    };
  }

  private defaults() {
    return {
      frequency: 'MONTHLY' as BillingFrequency,
      startPeriod: '',
      endPeriod: '',
      status: 'ACTIVE' as BillingConfigStatus,
      dueDayOfMonth: 20,
      collectionAllowanceEnabled: true,
      collectionAllowanceRate: 0.025,
      collectionAllowanceCap: 30,
      latePenaltyEnabled: true,
      latePenaltyRate: 0.10,
      latePenaltyMin: 50,
      latePenaltyMaxPct: 0,     // no cap by default (Todd 2026-10-05)
      lateInterestEnabled: false, // the annual accrual above the minimum is the interest; statutory is opt-in
      activeMonths: ''
    };
  }
}
